import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Alert,
  Pagination,
  Button,
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { formatStatus } from '../utils/formatStatus';
import { portalApi, InvoiceItem } from '../services/portalApi';
import { useTranslation } from 'react-i18next';
import { i18n } from '../i18n';

export const Invoices: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const limit = 10;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await portalApi.getInvoices({
          limit,
          offset: (page - 1) * limit,
        });
        setInvoices(response.items);
        setTotal(response.total);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(i18n.resolvedLanguage === 'es' ? 'es-MX' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number, invoice?: { currencyCode?: string; locale?: string }) => {
    return new Intl.NumberFormat(invoice?.locale || 'en-US', {
      style: 'currency',
      currency: invoice?.currencyCode || 'USD',
    }).format(amount);
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'sent':
        return 'warning';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('Invoices')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('View and pay your invoices')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box>
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i} sx={{ mb: 2 }}>
              <CardContent>
                <Skeleton width="30%" height={28} />
                <Skeleton width="60%" height={24} />
                <Skeleton width="40%" height={20} />
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : invoices.length === 0 ? (
        <Card>
          <CardContent sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              {t('No invoices yet')}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              {t('Your invoices will appear here')}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {invoices.map((invoice) => (
            <Card
              key={invoice.id}
              sx={{
                mb: 2,
                cursor: 'pointer',
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: 3 },
              }}
              onClick={() => navigate(`/invoices/${invoice.id}`)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Typography variant="h6">
                        {t('Invoice')} #{invoice.invoiceNumber}
                      </Typography>
                      <Chip
                        label={t(formatStatus(invoice.status))}
                        size="small"
                        color={getStatusColor(invoice.status)}
                      />
                    </Box>

                    <Typography color="text.secondary" sx={{ mb: 1 }}>
                      {t('Issued')}: {formatDate(invoice.issueDate)}
                      {invoice.dueDate && ` • ${t('Due Date')}: ${formatDate(invoice.dueDate)}`}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">{t('Total')}</Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {formatCurrency(invoice.total, invoice)}
                        </Typography>
                      </Box>
                      {invoice.status !== 'void' && invoice.amountDue > 0 && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">{t('Amount Due')}</Typography>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, color: 'warning.main' }}
                          >
                            {formatCurrency(invoice.amountDue, invoice)}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {invoice.pdfUrl && (
                      <Button
                        size="small"
                        startIcon={<PdfIcon />}
                        sx={{ mt: 2 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(invoice.pdfUrl!, '_blank');
                        }}
                      >
                        {t('Download PDF')}
                      </Button>
                    )}
                  </Box>

                  <ChevronRightIcon sx={{ color: 'text.secondary', ml: 2 }} />
                </Box>
              </CardContent>
            </Card>
          ))}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
